import { useEffect, useState } from "react";

import { keyBy, sortBy } from "lodash";
import { v4 as uuidv4 } from "uuid";

import { API, graphqlOperation } from "aws-amplify";
import {
  accountByActiveByName,
  accountByEmail,
  accountByLotPrefix,
  listAccounts,
  searchAccounts,
} from "../../../graphql/queries";
import { addressByAccountByNameByStreet } from "../../../graphql/queries";
import {
  createAccount,
  updateAccount,
  updateSavedAddress,
} from "../../../graphql/mutations";
import { productByAccountByActiveByName } from "../../../graphql/queries";
import {
  performUpdateAccount,
  performUpdateAccountMini,
} from "../../../graphql/account/accountCRUD";
import {
  getAccount,
  getAccountBillTo,
} from "../../../graphql/account/account-queries";

import { getCustomFieldId } from "../../../components/common/CustomFields";
import { logError, logHistory } from "../../../utils/appUtils";
import {
  AccountStatus,
  AccountType,
  CatalogHeaderMap,
  CustomFieldsDisplay,
  DefaultCarlotLotNumber,
  DefaultFifoLotNumber,
  ExternalStatus,
  InvoiceDefaultSubject,
  MAX_ROWS_UPLOAD,
  PODDefaultSubject,
  POSDefaultSubject,
  ResourceType,
  VALIDATION_MSG,
} from "../../../utils/constants";
import { trimEmail } from "../../../utils/utils";
import { createAccessorialCharges } from "../services/ServiceUtils";

// Billing email syncs to the external system as the customer's primary email,
// so it is required once the account syncs. required() alone would accept the
// " " placeholder that earlier imports stored as billing email.
export const requiredEmail = Object.assign(
  (value) => (trimEmail(value) ? undefined : "ra.validation.required"),
  { isRequired: true },
);

// CountryList ids ("USA" | "CA" | "MX") for common spellings from CSVs / external
// systems; unrecognized values are left as they came.
const COUNTRY_ALIASES = {
  USA: [
    "usa",
    "us",
    "u.s.",
    "u.s.a.",
    "united states",
    "united states of america",
  ],
  CA: ["ca", "can", "canada"],
  MX: ["mx", "mex", "mexico", "méxico"],
};
export const normalizeCountry = (value) => {
  const raw = typeof value === "string" ? value.trim() : value;
  if (!raw) return raw;
  const key = raw.toLowerCase();
  const id = Object.keys(COUNTRY_ALIASES).find((k) =>
    COUNTRY_ALIASES[k].includes(key),
  );
  return id || raw;
};

// Import-trigger rows: country to a select id, and one email on both
// `email` and `billingEmail`. New rows are in CSV shape (`Email`, `Country`);
// matched rows are existing accounts merged with the CSV (`billingEmail`,
// `address.country`).
export const prepareImportedCustomer = (customer) => {
  if (!customer.reloadYardId) {
    return { ...customer, Country: normalizeCountry(customer["Country"]) };
  }
  const email = customer.billingEmail || customer.email;
  return {
    ...customer,
    ...(email ? { email, billingEmail: email } : {}),
    address: customer.address && {
      ...customer.address,
      country: normalizeCountry(customer.address.country),
    },
  };
};

// Store websites as-is if they already include http(s),
// otherwise prepend https:// (allows users to enter "example.com")
export const normalizeWebsiteUrl = (value) => {
  if (value === null || value === undefined) return undefined;
  const raw = value.toString().trim();
  if (raw === "") return undefined;

  if (/^https?:\/\//i.test(raw)) return raw;
  if (/^\/\//.test(raw)) return `https:${raw}`;

  // Reject non-http(s) schemes (e.g. ftp:, file:, javascript:)
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(raw)) return undefined;

  return `https://${raw}`;
};

export const customHeadersToInventoryFields = async (
  form,
  customHeaders,
  record,
) => {
  const inventoryFieldState = form.getFieldState("inventoryCustomFields");
  let inventoryCustomFields = inventoryFieldState.value;

  if (customHeaders?.length > 0) {
    const inventoryFieldMap =
      inventoryCustomFields?.length > 0
        ? keyBy(inventoryCustomFields, "id")
        : {};
    customHeaders.forEach((header) => {
      const hid = getCustomFieldId(header);
      const match = inventoryFieldMap[hid];
      if (!match) {
        const newField = {
          id: getCustomFieldId(header),
          name: header,
          display: [CustomFieldsDisplay.MOBILE, CustomFieldsDisplay.TALLYSHEET],
        };
        if (inventoryCustomFields?.length > 0)
          inventoryCustomFields.push(newField);
        else inventoryCustomFields = [newField];
      }
    });

    form.change("inventoryCustomFields", inventoryCustomFields);

    if (record) {
      // History will be logged via logHistory after successful update
      const input = {
        id: record.id,
        inventoryCustomFields:
          inventoryCustomFields?.length > 0 ? inventoryCustomFields : undefined,
      };
      const result = await API.graphql(
        graphqlOperation(updateAccount, { input }),
      );

      // Log history for the account update
      const historyNote = {
        parentId: record.id,
        parentType: ResourceType.ACCOUNT,
        text: "Product Catalog imported.",
      };
      logHistory(historyNote);

      return result;
    }
  }
};

export const formValidate = async (form, notify) => {
  if (form.getState().invalid) {
    notify(VALIDATION_MSG, "warning");
  }
};

// Resolves once final-form has no async field validators in flight, so a
// save handler can trust form.getState().invalid.
export const waitForFormValidation = (form) =>
  new Promise((resolve) => {
    let unsubscribe;
    let done = false;
    const finish = () => {
      done = true;
      if (unsubscribe) unsubscribe();
      resolve();
    };
    unsubscribe = form.subscribe(
      (state) => {
        if (!state.validating && !done) finish();
      },
      { validating: true },
    );
    if (done) unsubscribe();
  });

export const getCustomHeaders = (product) => {
  const allColumns = Object.keys(product);
  const mapColumns = new Set(Object.values(CatalogHeaderMap));
  const customHeaders = allColumns.filter(
    (itm) =>
      itm !== "error" && itm !== "commodityCateory" && !mapColumns.has(itm),
  );
  return customHeaders;
};

/**
 * validateAccounNameUnicity
 *
 * Validates that the `businessName` and `displayName` fields of an account
 * conform to the following unicity rules within the current reload yard:
 *
 * 1. `businessName` must be unique **only if** `displayName` is blank or equals `businessName`.
 *    - This allows multiple accounts to use the same `businessName` only if they provide a distinct `displayName`.
 *
 * 2. `displayName` must always be unique across all accounts.
 *    - It cannot match another account’s `displayName` or `businessName`.
 *    - This ensures display names are truly distinct and avoids confusion.
 *
 * The check excludes the current record when editing (`formData.id`).
 *
 * @param {string} source - The name of the field being validated (e.g. "businessName" or "displayName")
 * @param {object} form - React Final Form object (used to set field errors)
 * @param {object} formData - Current values of the form fields (includes `id`, `businessName`, `displayName`)
 *
 * Example: Allowed combinations
 * ┌────────────────────┬────────────────────┐
 * │ businessName       │ displayName        │
 * ├────────────────────┼────────────────────┤
 * │ NVR Homes          │ NVR East           │ ✅ Allowed
 * │ NVR Homes          │ NVR West           │ ✅ Allowed
 * │ Exxon              │ Exxon              │ ✅ Allowed once
 * │ Cargill, Inc       │ (blank)            │ ✅ Allowed
 * │ Sitka Forest       │ Sitka              │ ✅ Allowed
 *
 * Example: Disallowed combinations
 * ┌────────────────────┬────────────────────┐
 * │ businessName       │ displayName        │
 * ├────────────────────┼────────────────────┤
 * │ Cargil             │ (blank)            │ ❌ Duplicate businessName (no displayName)
 * │ Cargil             │ (blank)            │ ❌
 * │ NVR Homes          │ NVR                │ ❌ displayName "NVR" used elsewhere
 * │ NVR                │ NVR Homes          │ ❌ displayName "NVR Homes" used elsewhere
 * │ Exxon              │ (blank)            │ ❌ if another Exxon with no distinct displayName exists
 * │ Exxon              │ Exxon              │
 */
export const validateAccounNameUnicity = async (source, form, formData) => {
  const businessName = formData.businessName?.trim();
  const displayName = formData.displayName?.trim();
  const currentId = formData.id;

  const sourceValue = formData[source]?.trim();
  if (!sourceValue) {
    form.mutators.setFieldData(source, { error: false });
    return;
  }

  try {
    const defaultFilter = {
      reloadYardId: { eq: window.appUser.reloadYardId },
    };

    if (currentId) {
      defaultFilter.id = { ne: currentId }; // exclude self when editing
    }

    const or = [];

    // Rule 1: businessName must be unique unless displayName is different
    if (businessName && (!displayName || displayName === businessName)) {
      or.push({ businessName: { eq: businessName } });
    }

    // Rule 2: displayName must be unique from all other displayNames and businessNames
    if (displayName) {
      or.push({ businessName: { eq: displayName } });
      or.push({ displayName: { eq: displayName } });
    }

    const filter = {
      ...defaultFilter,
      ...(or.length > 0 && { or }),
    };

    const response = await API.graphql(
      graphqlOperation(searchAccounts, {
        filter,
        limit: 1,
      }),
    );

    const total = response?.data?.searchAccounts?.total || 0;

    if (total > 0) {
      form.mutators.setFieldData(source, {
        error: "Duplicate account name detected.",
      });
    } else {
      form.mutators.setFieldData(source, { error: false });
    }
  } catch (error) {
    logError("accounts:accountUtils:validateAccounNameUnicity", error, false);
  }
};

export const validateEmailUnicity = async (value, allValues) => {
  if (value) {
    try {
      let resp = await API.graphql(
        graphqlOperation(accountByEmail, {
          reloadYardId: window.appUser?.reloadYardId,
          email: { eq: value.toLowerCase() },
        }),
      );

      if (
        resp.data.accountByEmail.items.length !== 0 &&
        resp.data.accountByEmail.items[0].id !== allValues.id
      ) {
        return {
          message: `Email: ${value} is already used for: ${resp.data.accountByEmail.items[0].businessName}. Continue saving petentailly duplicate Account?`,
        };
      }
    } catch (error) {
      logError("accounts:AccountEdit:validateEmailUnicity", error, false);
    }
  }
  return null;
};

export const validateLotPrefixUnicity = async (value, allValues) => {
  if (value && value !== "") {
    try {
      let resp = await API.graphql(
        graphqlOperation(accountByLotPrefix, {
          reloadYardId: window.appUser?.reloadYardId,
          lotNumberPrefix: { eq: value.toUpperCase() },
        }),
      );

      if (
        resp.data.accountByLotPrefix.items.length !== 0 &&
        resp.data.accountByLotPrefix.items[0].id !== allValues.id
      ) {
        const owner = resp.data.accountByLotPrefix.items[0].businessName;
        return {
          message: `Lot No. Prefix: ${value} is already used for: ${owner}`,
          fieldMessage: `Already used by ${owner}`,
        };
      }
    } catch (error) {
      logError("accounts:AccountCreate:validateLotPrefixUnicity", error, false);
    }
  }
  return null;
};

// Field-level validator for lotNumberPrefix (react-admin composes async
// validators). final-form re-runs every field validator on every change
// anywhere in the form, so answer synchronously from the last result unless
// the prefix or the record changed; only a new prefix costs a query.
let lastLotPrefixCheck = { key: null, error: undefined };
const lotPrefixKey = (value, allValues) =>
  `${allValues?.id || ""}|${(value || "").toUpperCase()}`;
export const validateLotPrefixUnique = (value, allValues) => {
  const key = lotPrefixKey(value, allValues);
  if (lastLotPrefixCheck.key === key) return lastLotPrefixCheck.error;
  return validateLotPrefixUnicity(value, allValues).then((dup) => {
    const error = dup ? dup.fieldMessage : undefined;
    lastLotPrefixCheck = { key, error };
    return error;
  });
};

// Save-time check that ignores the cache: the prefix may have been taken since
// the field was last validated. Resolves true when the prefix is taken; the
// result is seeded into the cache and the field re-validated so the error
// shows on the field and the form reads invalid.
export const lotPrefixTakenAtSave = async (form) => {
  const values = form.getState().values;
  const dup = await validateLotPrefixUnicity(values.lotNumberPrefix, values);
  const error = dup ? dup.fieldMessage : undefined;
  lastLotPrefixCheck = {
    key: lotPrefixKey(values.lotNumberPrefix, values),
    error,
  };
  if (!error) return false;
  // Any mutator call makes final-form re-run every field validator, which now
  // answers from the seeded cache.
  form.mutators.setFieldData("lotNumberPrefix", {});
  return true;
};

export const fetchSavedAddresses = async (
  accountId,
  notify,
  setLoading,
  setAllAddresses,
) => {
  let allAddresses = [];
  let nextToken = null;
  setLoading && setLoading(true);

  try {
    do {
      // Built fresh each page so the token just returned is the one sent.
      const addressQuery = {
        accountId: accountId,
        limit: 501,
        ...(nextToken && { nextToken }),
      };

      const result = await API.graphql(
        graphqlOperation(addressByAccountByNameByStreet, addressQuery),
      );
      const addresses = result.data.addressByAccountByNameByStreet.items;
      nextToken = result.data.addressByAccountByNameByStreet.nextToken;

      if (addresses.length > 0) {
        allAddresses = allAddresses.concat(addresses);
      }
    } while (nextToken);
  } catch (error) {
    logError("accountUtils:fetchSavedAddresses", error, notify);
  } finally {
    setLoading && setLoading(false);
    setAllAddresses && setAllAddresses(allAddresses);
  }
  return allAddresses;
};

export const updateSavedAddressRecord = async (input) => {
  const cleaned = { ...input };
  delete cleaned.createdAt;
  delete cleaned.updatedAt;
  const result = await API.graphql(
    graphqlOperation(updateSavedAddress, { input: cleaned }),
  );
  return result?.data?.updateSavedAddress;
};

export const fetchAccounts = async (notify, setAccounts, setLoading) => {
  const limit = 500;
  let data = [];
  let nextToken = null;
  debugger;
  try {
    setLoading && setLoading(true);
    do {
      const result = await API.graphql(
        graphqlOperation(listAccounts, {
          limit,
          nextToken,
        }),
      );

      nextToken = result.data.listAccounts.nextToken;
      data = data.concat(result.data.listAccounts.items);
    } while (nextToken);

    data = sortBy(data, (acc) => acc.businessName?.toLowerCase() || "");
    setAccounts && setAccounts(data);
  } catch (error) {
    logError("accounts:AccountUtils:fetchAccounts", error, notify);
  } finally {
    setLoading && setLoading(false);
  }
  return data;
};

export const fetchActiveAccounts = async (notify) => {
  const limit = 500;
  let data = [];
  let nextToken = null;
  try {
    do {
      const input = {
        reloadYardId: window.appUser.reloadYardId,
        activeBusinessName: { beginsWith: { active: "true" } },
        limit,
        nextToken,
      };
      const result = await API.graphql(
        graphqlOperation(accountByActiveByName, input),
      );
      const payload = result?.data?.accountByActiveByName;
      data = data.concat(payload?.items || []);
      nextToken = payload?.nextToken;
    } while (nextToken);
  } catch (error) {
    logError(
      "chargeRules:ImportChargeRulesMenuButton:fetchActiveAccounts",
      error,
      notify,
    );
  }
  return data;
};

export const isSameAccount = (account, customer) => {
  const normalize = (str) =>
    (str ?? "") // fallback to empty string if null/undefined
      .toString()
      .replace(/[^\w]|_/g, "") // remove punctuation, whitespace, underscore
      .toLowerCase();

  const accountName = normalize(account?.businessName);
  const displayName = normalize(account?.displayName);
  const customerName = normalize(customer?.["Company name"]);
  const customerDisplay = normalize(customer?.["Name"]);

  return (
    (accountName &&
      (accountName === customerName || accountName === customerDisplay)) ||
    (displayName &&
      (displayName === customerName || displayName === customerDisplay))
  );
};

export const isSameCustomer = (a, b) => {
  const normalize = (str) =>
    (str ?? "") // fallback to empty string if null/undefined
      .toString()
      .replace(/[^\w]|_/g, "") // remove punctuation, whitespace, underscore
      .toLowerCase();

  const aName = normalize(a?.["Customer name"]);
  const aDisplay = normalize(a?.["Name"]);
  const bName = normalize(b?.["Company name"]);
  const bDisplay = normalize(b?.["Name"]);

  return (aName && aName === bName) || (aDisplay && aDisplay === bDisplay);
};

/**
 * Helper to create an Account from a customer CSV row
 * @param {Object} customer - Customer row from CSV
 * @param {String} reloadYardId - Current reload yard context
 * @returns {Promise<Object>} - Created account
 */
export const createAccountFromCustomer = async (customer, externalStatus) => {
  // Canonical business name: dialog merge always sets `businessName` first.
  // For legacy / external rows (e.g. QBO-oriented shape), `Name` is the primary
  // label before `Company name`. Order must not prefer Company over Name when
  // `businessName` is absent.
  const resolvedBusinessName = (
    customer.businessName ??
    customer["Name"] ??
    customer["Company name"]
  )
    ?.toString()
    .trim();
  const resolvedDisplayName = customer.displayName?.toString?.().trim?.();

  const newAccountInput = {
    id: uuidv4(),
    reloadYardId: window.appUser.reloadYardId,
    groups: window.appUser.groups,
    active: "false",
    status: AccountStatus.NEW,
    businessName: resolvedBusinessName,
    ...(resolvedDisplayName ? { displayName: resolvedDisplayName } : {}),
    externalId: " ",
    externalStatus: externalStatus,
    email: customer["Email"] || " ",
    phone: customer["Phone"] || undefined,
    billingEmail: customer["Email"] || undefined,
    address: {
      name: "",
      street: customer["Street Address"] || "",
      city: customer["City"] || "",
      state: customer["State"] || "",
      zipCode: customer["Zip"] || "",
      country: customer["Country"] || "",
    },
    billingAddress: {
      name: "",
      street: customer["Street Address"] || "",
      city: customer["City"] || "",
      state: customer["State"] || "",
      zipCode: customer["Zip"] || "",
      country: customer["Country"] || "",
    },
    addressState: customer["State"] || "",
    podSubject: JSON.stringify(PODDefaultSubject),
    posSubject: JSON.stringify(POSDefaultSubject),
    invoiceSubject: JSON.stringify(InvoiceDefaultSubject),
    skipInvoice: false,
    fifoLotNumber: JSON.stringify(DefaultFifoLotNumber),
    carlotLotNumber: JSON.stringify(DefaultCarlotLotNumber),
  };

  const response = await API.graphql(
    graphqlOperation(createAccount, { input: newAccountInput }),
  );

  const createdAccount = response.data.createAccount;

  // Log history for the imported account
  const historyNote = {
    parentId: createdAccount.id,
    parentType: ResourceType.ACCOUNT,
    text: "Account Imported",
  };
  logHistory(historyNote);

  await createAccessorialCharges(createdAccount);

  return createdAccount;
};

export const updateAccountFromCustomer = async (customer) => {
  debugger;
  const response = await performUpdateAccount(customer);
  // Log history for the imported account
  const historyNote = {
    parentId: response.data.updateAccount.id,
    parentType: ResourceType.ACCOUNT,
    text: "Account Updated on import",
  };
  logHistory(historyNote);
  return response.data.updateAccount;
};

// Fetch products for the account (needed for DeepDeleteButton on all screen sizes)
// Too many products to bring back by direct @connection (max 100)
// DIrect fetch allows account to load faster.
export const fetchProducts = async (accountId, activeOnly = true) => {
  if (!accountId) throw new Error("Account ID is required");
  let allItems = [];
  let nextToken = null;

  do {
    const input = {
      accountId: accountId,
      ...(activeOnly && {
        activeName: { beginsWith: { active: "true" } },
      }),
      limit: MAX_ROWS_UPLOAD,
      nextToken,
    };

    const resp = await API.graphql(
      graphqlOperation(productByAccountByActiveByName, input),
    );
    const productData = resp.data.productByAccountByActiveByName;
    allItems = [...allItems, ...productData.items];
    nextToken = productData.nextToken;
  } while (nextToken);
  return allItems;
};

export const setAccountSyncPending = async (account) => {
  try {
    const accountInput = {
      id: account.id,
      externalStatus: ExternalStatus.SYNC_PENDING,
    };
    await performUpdateAccountMini(accountInput);
  } catch (err) {}
};

export const setAccountSyncFailed = async (account) => {
  try {
    const accountInput = {
      id: account.id,
      externalStatus: ExternalStatus.SYNC_FAILED,
    };
    await performUpdateAccountMini(accountInput);
  } catch (err) {}
};

export const isInternalAccount = (account) => {
  return account?.accountType === AccountType.INTERNAL;
};

export const isBrokeredAccount = (account) => {
  return account?.accountType === AccountType.BROKERED;
};

// Effective skipInvoice for a NEW shipment of this account.
// Account-level skipInvoice means "never invoice this account directly" —
// picking BROKERED/INTERNAL in AccountContactTab force-sets it. But a
// brokered shipment bills to the broker (defaultBillToAccountId), so the
// shipment-level flag must follow the broker, not the operating account;
// copying the operating account's flag hides billing and blocks invoicing
// for every brokered shipment. Broker not resolvable => default billable.
export const getEffectiveSkipInvoice = (account, billToAccount) =>
  isBrokeredAccount(account) && account?.defaultBillToAccountId
    ? Boolean(billToAccount?.skipInvoice)
    : Boolean(account?.skipInvoice);

// Resolves the account a NEW shipment of this account bills to: the broker
// behind a BROKERED account's defaultBillToAccountId. Every other account
// type bills the shipment's own account => null. knownAccounts (a dropdown
// list) answers without a fetch when it can, but that list is active-only
// and capped, so a miss there is not proof the broker is gone — fall through
// to a fetch so an archived or paginated-out broker still resolves. Truly
// unresolvable (deleted id) => logged null, and getEffectiveSkipInvoice
// defaults the shipment to billable.
export const resolveBillToAccount = async (account, knownAccounts = []) => {
  if (!isBrokeredAccount(account) || !account?.defaultBillToAccountId)
    return null;
  const billToId = account.defaultBillToAccountId;

  const known = knownAccounts.find((a) => a.id === billToId);
  if (known) return known;

  try {
    const resp = await API.graphql(
      graphqlOperation(getAccountBillTo, { id: billToId }),
    );
    const billTo = resp?.data?.getAccount ?? null;
    if (!billTo) {
      logError(
        "accounts:AccountUtils:resolveBillToAccount",
        new Error(`defaultBillToAccountId ${billToId} not found`),
        false,
      );
    }
    return billTo;
  } catch (err) {
    logError("accounts:AccountUtils:resolveBillToAccount", err, false);
    return null;
  }
};

// Resolves billToAccountId for a NEW shipment from the account's type alone, so a
// stale form value can never leak into billing. The create forms only expose a
// billing picker for INTERNAL accounts; for the other types whatever the form
// holds is ignored — react-admin's defaultValue effect once wrote a previously
// selected broker back into a hidden picker after an account switch (prod
// inbound 8c5e4372, 2026-09-14), and the save trusted it.
//   INTERNAL  => the picked billing account, else the account itself
//   BROKERED  => the account's defaultBillToAccountId, else the account itself
//   CUSTOMER  => the account itself, always
export const resolveCreateBillToAccountId = (account, pickedBillToId) => {
  if (!account?.id) return pickedBillToId || null;
  if (isInternalAccount(account)) return pickedBillToId || account.id;
  if (isBrokeredAccount(account))
    return account.defaultBillToAccountId || account.id;
  return account.id;
};

// Returns the account a shipment RECORD bills to: billToAccountId when set (the broker
// for BROKERED, the picked billing account for INTERNAL), else the shipment's own account.
// This is the same resolution invoicing uses (see InvoiceUtils.fetchAccountMap), so any
// charge-rule preview must go through it too — resolving rules by another account shows
// charges the invoice will never apply.
export const getBillingAccountId = (shipment) =>
  shipment?.billToAccountId || shipment?.accountId || null;

// Returns the account responsible for operations (inventory, POs, services, templates).
// For INTERNAL outbounds the operating account is the billToAccount (the customer the yard
// acts on behalf of). For all other cases it is the accountId account.
export const getOperatingAccountId = (shipment) => {
  if (
    shipment?.account?.accountType === AccountType.INTERNAL &&
    shipment?.billToAccountId
  ) {
    return shipment.billToAccountId;
  }
  return shipment?.accountId ?? null;
};

// Hook for resolving the operating account on an outbound/show component.
// Fetches billToAccount only when the shipment is from an INTERNAL account AND
// billToAccountId differs from accountId — the one case where account !== operatingAccount.
// Returns [operatingAccount, setOperatingAccount] so callers can override it (e.g. on
// billing-account picker change in the edit form).
export const useOperatingAccount = (record) => {
  const [operatingAccount, setOperatingAccount] = useState(
    record?.account || null,
  );

  useEffect(() => {
    const account = record?.account;
    if (!account) return;
    const billToId = record?.billToAccountId;
    const accountId = record?.accountId;
    if (isInternalAccount(account) && billToId && billToId !== accountId) {
      let cancelled = false;
      API.graphql(graphqlOperation(getAccount, { id: billToId }))
        .then((res) => {
          if (!cancelled) setOperatingAccount(res.data?.getAccount || account);
        })
        .catch(() => {
          if (!cancelled) setOperatingAccount(account);
        });
      return () => {
        cancelled = true;
      };
    } else {
      setOperatingAccount(account);
    }
  }, [record?.id, record?.account, record?.billToAccountId, record?.accountId]);

  return [operatingAccount, setOperatingAccount];
};
